import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * De koppeling tussen de skip-link bovenin en de verantwoordingsvoet onderaan.
 *
 * `EXPERIENCE.md` (*Accessibility Floor*) vraagt dat de voet met het toetsenbord bereikbaar is
 * zonder eerst de tabellen te doorlopen. Die link hoort bovenaan de module te staan — vóór de
 * tabbalk en vóór de inhoud — terwijl de voet die hij aanwijst in de weergave eronder wordt
 * getekend, en op sommige toestanden (laden, een storing) helemaal niet.
 *
 * **Een skip-link naar een anker dat er niet is, is erger dan geen skip-link:** hij belooft een
 * sprong en levert een pagina die niet beweegt. Vandaar deze melding: de voet zegt dat hij bestaat,
 * de schil tekent de link alleen dan. Eén boolean, geen bibliotheek.
 */

export const VERANTWOORDING_ANKER = 'werkkwaliteit-verantwoording';

interface Melding {
  aanwezig: boolean;
  meld: (aan: boolean) => void;
}

const Context = createContext<Melding>({ aanwezig: false, meld: () => {} });

export function VerantwoordingProvider({ children }: { children: ReactNode }) {
  // Een teller en geen boolean. Twee voeten tegelijk komt in de huidige routetabel niet voor — de
  // vier weergaven sluiten elkaar uit — maar een boolean die de vertrekkende voet op `false` zet
  // zou de link laten vallen zodra dat wél gebeurt, en een teller kost hier niets.
  const [aantal, setAantal] = useState(0);
  const meld = useCallback((aan: boolean) => setAantal((vorig) => Math.max(0, vorig + (aan ? 1 : -1))), []);
  const waarde = useMemo(() => ({ aanwezig: aantal > 0, meld }), [aantal, meld]);

  return <Context.Provider value={waarde}>{children}</Context.Provider>;
}

/** True zodra er ergens onder deze schil een verantwoordingsvoet staat. */
export function useHeeftVerantwoording(): boolean {
  return useContext(Context).aanwezig;
}

/**
 * Meldt zolang het component leeft dat de voet er is.
 *
 * `useLayoutEffect` en niet `useEffect`: met de gewone variant staat de link er pas één tekening
 * later, en wie meteen begint te tabben komt hem dan niet tegen. Bij het wisselen van weergave
 * vallen het afmelden en het aanmelden zo bovendien in dezelfde vastlegging, zodat de link niet
 * kort verdwijnt.
 */
export function useMeldVerantwoording(): void {
  const { meld } = useContext(Context);
  useLayoutEffect(() => {
    meld(true);
    return () => meld(false);
  }, [meld]);
}
